import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from 'recharts';
import { ChartCard, ChartEmpty } from './ChartCard';

interface Point { time: string; price: number; }
interface FearGreed { value: number; classification: string }

interface Props {
  btc: Point[];
  eth: Point[];
  fearGreed: FearGreed | null;
}

/** USD 정수 + 천단위 콤마 + $ (Y축/툴팁 공통). 예: 82347.70 → $82,348 */
const fmtUsd = (v: number) => `$${Math.round(v).toLocaleString()}`;

/** 모닝브리핑 코인 차트 X축 tick — 어제 11:00 KST ~ 오늘 08:00 KST 윈도우의 3시간 단위.
 *  데이터 5분봉 안 해당 시각이 존재하면 라벨로 노출. */
const COIN_X_TICKS = ['11:00', '14:00', '17:00', '20:00', '23:00', '02:00', '05:00', '08:00'];

/** F&G 색상 — 수치별 (0~24 극공포 / 25~44 공포 / 45~54 중립 / 55~74 탐욕 / 75~100 극탐욕) */
function fearGreedColor(v: number): string {
  if (v < 25) return '#dc2626';
  if (v < 45) return '#f97316';
  if (v < 55) return '#94a3b8';
  if (v < 75) return '#16a34a';
  return '#15803d';
}

/** Alternative.me F&G classification(영문) → 한글 라벨. */
function fearGreedKr(en: string): string {
  const map: Record<string, string> = {
    'Extreme Fear': '극공포',
    'Fear':         '공포',
    'Neutral':      '중립',
    'Greed':        '탐욕',
    'Extreme Greed':'극탐욕',
  };
  return map[en.trim()] ?? en;
}

/** 코인 24H — BTC/ETH 시계열 + F&G 게이지 */
export function CoinChart({ btc, eth, fearGreed }: Props) {
  const fg = fearGreed
    ? [{ name: 'F&G', value: fearGreed.value, fill: fearGreedColor(fearGreed.value) }]
    : [];
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <ChartCard title="BTC 24H" subtitle="USD">
        {btc.length === 0 ? (
          <ChartEmpty />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={btc} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} ticks={COIN_X_TICKS} interval={0} />
              <YAxis
                domain={['dataMin', 'dataMax']}
                tick={{ fontSize: 11 }}
                width={80}
                tickFormatter={fmtUsd}
              />
              <Tooltip formatter={(v: number) => [fmtUsd(v), '가격']} labelFormatter={(l: string) => `시간 ${l}`} />
              <Line type="monotone" dataKey="price" stroke="#f7931a" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="ETH 24H" subtitle="USD">
        {eth.length === 0 ? (
          <ChartEmpty />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={eth} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} ticks={COIN_X_TICKS} interval={0} />
              <YAxis
                domain={['dataMin', 'dataMax']}
                tick={{ fontSize: 11 }}
                width={70}
                tickFormatter={fmtUsd}
              />
              <Tooltip formatter={(v: number) => [fmtUsd(v), '가격']} labelFormatter={(l: string) => `시간 ${l}`} />
              <Line type="monotone" dataKey="price" stroke="#3b82f6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="공포·탐욕 지수"
        subtitle={fearGreed ? fearGreedKr(fearGreed.classification) : '데이터 없음'}
      >
        {!fearGreed ? (
          <ChartEmpty />
        ) : (
          <div className="relative">
            <ResponsiveContainer width="100%" height={220}>
              <RadialBarChart
                cx="50%"
                cy="55%"
                innerRadius="65%"
                outerRadius="90%"
                barSize={20}
                data={fg}
                startAngle={180}
                endAngle={0}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                <RadialBar background dataKey="value" cornerRadius={10} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-x-0 bottom-6 text-center">
              <div className="text-3xl font-extrabold" style={{ color: fearGreedColor(fearGreed.value) }}>
                {fearGreed.value}
              </div>
              <div className="text-xs text-[var(--color-text-sub)]">/ 100</div>
            </div>
          </div>
        )}
      </ChartCard>
    </div>
  );
}
